import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Users, Search as SearchIcon, DollarSign, Coins, Loader2, Radar, Mail, Share2, Heart, Crown, Clock } from "lucide-react";
import { toast } from "sonner";

export default function AdminPanel() {
  const [stats, setStats] = useState({ total_users: 0, total_searches: 0, total_payments: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState(50);
  const [reason, setReason] = useState("Créditos concedidos pelo administrador");
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [runningAlerts, setRunningAlerts] = useState(false);
  const [runningEndings, setRunningEndings] = useState(false);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openGrant = (u) => { setTarget(u); setAmount(50); setReason("Créditos concedidos pelo administrador"); setOpen(true); };

  const grant = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/grant-credits", {
        user_id: target.id, amount: Number(amount), reason,
      });
      toast.success(`Novo saldo: ${data.new_balance} créditos`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const runScrape = async () => {
    setScraping(true);
    try {
      const { data } = await api.post("/admin/scrape");
      const counts = data.counts;
      toast.success(`Scraping concluído: leilaovip=${counts.leilaovip}, superbid=${counts.superbid}, portalzuk=${counts.portalzuk} (total: ${data.total})`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setScraping(false); }
  };

  const runAlerts = async () => {
    setRunningAlerts(true);
    try {
      const { data } = await api.post("/alerts/run-all");
      toast.success(`Alertas enviados: ${data.sent} · sem match: ${data.skipped}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setRunningAlerts(false); }
  };

  const runEndings = async () => {
    setRunningEndings(true);
    try {
      await api.post("/admin/run-auction-endings");
      toast.success("Avisos de encerramento disparados (janela de 24h)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setRunningEndings(false); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-emerald-400" />
          <h1 className="font-display text-3xl font-bold text-white">Painel Administrativo</h1>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Usuários" value={stats.total_users} color="emerald" />
          <StatCard icon={SearchIcon} label="Pesquisas totais" value={stats.total_searches} color="amber" />
          <StatCard icon={DollarSign} label="Pagamentos concluídos" value={stats.total_payments} color="purple" />
        </div>

        {/* Operations */}
        <div className="mt-6 p-5 rounded-xl bg-slate-900/70 border border-slate-800">
          <h2 className="font-display text-lg font-bold text-white">Operações</h2>
          <div className="mt-3 flex flex-wrap gap-3">
            <Button onClick={runScrape} disabled={scraping} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="run-scrape-btn">
              {scraping ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Radar className="w-4 h-4 mr-2" />}
              Executar Scraping
            </Button>
            <Button onClick={runAlerts} disabled={runningAlerts} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="run-alerts-btn">
              {runningAlerts ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Disparar Todos Alertas
            </Button>
            <Button onClick={runEndings} disabled={runningEndings} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="run-endings-btn">
              {runningEndings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
              Avisar Encerramentos (24h)
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">Cron automático: alertas 09:00 · encerramentos toda hora :05 (America/São_Paulo).</p>
        </div>

        {/* Analytics */}
        {analytics && (
          <div className="mt-6 space-y-4" data-testid="analytics-section">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard icon={Crown} label="Assinantes Pro" value={analytics.pro_subscribers} color="purple" />
              <StatCard icon={Heart} label="Total favoritos" value={analytics.total_favorites} color="amber" />
              <StatCard icon={Share2} label="Compartilhamentos" value={analytics.total_shares} color="emerald" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800" data-testid="top-shared">
                <h3 className="font-display text-sm font-bold text-white flex items-center gap-2"><Share2 className="w-4 h-4 text-emerald-400" />Mais compartilhados</h3>
                <div className="mt-3 divide-y divide-slate-800">
                  {analytics.top_shared.length === 0 ? (
                    <div className="py-4 text-sm text-slate-500">Nenhum compartilhamento ainda.</div>
                  ) : analytics.top_shared.map((s, i) => (
                    <div key={s.vehicle_id} className="py-2.5 flex items-center gap-3" data-testid={`shared-row-${s.vehicle_id}`}>
                      <div className="text-slate-500 font-mono text-sm w-6">#{i + 1}</div>
                      <img src={s.image} alt="" className="w-10 h-10 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">{s.brand} {s.model}</div>
                        <div className="text-xs text-slate-500">{s.year} · {s.uf}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-emerald-400 text-sm font-bold">{s.shares_total} shares</div>
                        <div className="text-xs text-slate-500">{s.views} views</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-5 rounded-xl bg-slate-900/70 border border-slate-800" data-testid="top-favorited">
                <h3 className="font-display text-sm font-bold text-white flex items-center gap-2"><Heart className="w-4 h-4 text-red-400" />Mais favoritados</h3>
                <div className="mt-3 divide-y divide-slate-800">
                  {analytics.top_favorited.length === 0 ? (
                    <div className="py-4 text-sm text-slate-500">Nenhum favorito ainda.</div>
                  ) : analytics.top_favorited.map((f, i) => (
                    <div key={f.vehicle_id} className="py-2.5 flex items-center gap-3" data-testid={`favorited-row-${f.vehicle_id}`}>
                      <div className="text-slate-500 font-mono text-sm w-6">#{i + 1}</div>
                      <img src={f.image} alt="" className="w-10 h-10 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">{f.brand} {f.model}</div>
                        <div className="text-xs text-slate-500">{f.year} · {f.uf}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-red-400 text-sm font-bold">{f.favorites}</div>
                        <div className="text-xs text-slate-500">favoritos</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 rounded-xl bg-slate-900/70 border border-slate-800 overflow-hidden">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-white">Usuários</h2>
            <span className="text-xs font-mono text-slate-400">{users.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60">
                <tr className="text-left">
                  <th className="p-3 text-xs uppercase tracking-widest text-slate-400 font-medium">Email</th>
                  <th className="p-3 text-xs uppercase tracking-widest text-slate-400 font-medium">Nome</th>
                  <th className="p-3 text-xs uppercase tracking-widest text-slate-400 font-medium">Role</th>
                  <th className="p-3 text-xs uppercase tracking-widest text-slate-400 font-medium">Créditos</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="p-3 text-slate-200">{u.email}</td>
                    <td className="p-3 text-slate-300">{u.name}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${u.role === "admin" ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-amber-400">{u.credits}</td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => openGrant(u)} className="border-slate-700 hover:bg-slate-800" data-testid={`grant-btn-${u.id}`}>
                        <Coins className="w-3.5 h-3.5 mr-1.5" />Conceder
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle>Conceder créditos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-400">Usuário: <span className="text-white font-medium">{target?.email}</span></div>
            <div>
              <Label>Quantidade (use negativo para remover)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800" data-testid="grant-amount-input" />
            </div>
            <div>
              <Label>Motivo</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800" data-testid="grant-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700">Cancelar</Button>
            <Button onClick={grant} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold" data-testid="grant-confirm-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: "text-emerald-400 bg-emerald-500/15 border-emerald-500/30",
    amber: "text-amber-400 bg-amber-500/15 border-amber-500/30",
    purple: "text-purple-400 bg-purple-500/15 border-purple-500/30",
  };
  return (
    <div className={`p-5 rounded-xl border ${colors[color]}`}>
      <Icon className="w-5 h-5" />
      <div className="mt-3 font-mono text-3xl font-bold text-white">{value}</div>
      <div className="text-xs uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}
